import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { forgotPasswordStart, forgotPasswordConfirm } from '../cognito';
import AuthLayout, { CodeInput, PasswordInput } from '../components/shell/AuthLayout';
import { Icon } from '../components/ui';

/**
 * Two-step Cognito forgot-password flow.
 *
 *   1. User enters their username → Cognito mails a 6-digit code to their
 *      verified email (the address on their Cognito user record).
 *   2. User pastes the code + types a new password → confirmPassword.
 *
 * Existing MFA enrolment is preserved across the reset — the next sign-in
 * still demands the TOTP code.
 */
const COPY = {
  start: { title: 'Reset your password', subtitle: 'Enter your username and we will email a 6-digit code to the address on file.' },
  confirm: { title: 'Check your email', subtitle: 'Enter the code we sent, then choose a new password of 12 or more characters.' },
  done: { title: 'Password reset', subtitle: 'Sign in with the new password. Your authenticator code is still required.' },
};

function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('start'); // start | confirm | done
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);

  async function submitStart(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await forgotPasswordStart(username);
    setLoading(false);
    if (result.status === 'codeSent') {
      setCognitoUser(result.cognitoUser);
      setStep('confirm');
    } else {
      setError(result.message || 'Failed to start reset');
    }
  }

  async function submitConfirm(e) {
    e.preventDefault();
    if (newPassword !== confirmPwd) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');
    const result = await forgotPasswordConfirm(cognitoUser, code, newPassword);
    setLoading(false);
    if (result.status === 'success') {
      setStep('done');
    } else {
      setError(result.message || 'Failed to reset password');
    }
  }

  const mismatch = confirmPwd.length > 0 && confirmPwd !== newPassword;

  return (
    <AuthLayout title={COPY[step].title} subtitle={COPY[step].subtitle}>
      {error && (
        <div className="notice notice-danger" role="alert">
          <Icon name="alertCircle" />
          <div className="notice-body">{error}</div>
        </div>
      )}

      {step === 'start' && (
        <form onSubmit={submitStart}>
          <div className="form-group">
            <label className="form-label" htmlFor="fp-username">Username</label>
            <input
              id="fp-username"
              type="text"
              className="form-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              spellCheck={false}
              autoFocus
              required
            />
          </div>
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading || !username}>
            {loading ? <><span className="spinner-sm" /> Sending…</> : 'Send code'}
          </button>
        </form>
      )}

      {step === 'confirm' && (
        <form onSubmit={submitConfirm}>
          <div className="form-group">
            <label className="form-label" htmlFor="fp-code">Verification code</label>
            <CodeInput id="fp-code" value={code} onChange={setCode} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="fp-new">New password</label>
            <PasswordInput id="fp-new" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                           autoComplete="new-password" minLength={12} autoFocus={false} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="fp-confirm">Confirm new password</label>
            <PasswordInput id="fp-confirm" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
                           autoComplete="new-password" />
            {mismatch && <div className="form-error">Does not match the new password</div>}
          </div>
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading || code.length !== 6}>
            {loading ? <><span className="spinner-sm" /> Resetting…</> : 'Reset password'}
          </button>
        </form>
      )}

      {step === 'done' && (
        <div className="stack">
          <div className="notice notice-ok mb-0">
            <Icon name="checkCircle" />
            <div className="notice-body">Your password has been changed.</div>
          </div>
          <button className="btn btn-primary btn-lg btn-block" onClick={() => navigate('/')}>
            Back to sign in
          </button>
        </div>
      )}

      {step !== 'done' && (
        <div className="auth-footer-link">
          <Link to="/"><Icon name="arrowLeft" size={14} /> Back to sign in</Link>
        </div>
      )}
    </AuthLayout>
  );
}

export default ForgotPassword;
